import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  ExternalLink, 
  Instagram, 
  Youtube, 
  Users,
  DollarSign,
  RefreshCw,
  Loader2,
  Search
} from "lucide-react";
import { FaTiktok } from "react-icons/fa";

interface Influencer {
  id: string;
  name: string;
  category: string;
  description: string | null;
  followers: string;
  price: number;
  image: string | null;
  rating: number;
  expertise: string[];
  social_links: {
    instagram?: string;
    youtube?: string;
    tiktok?: string;
  };
  contact_info: {
    email?: string;
    phone?: string;
  };
  approval_status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
  user_id: string;
}

export const InfluencerApprovalPanel = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; influencer: Influencer | null }>({
    open: false,
    influencer: null
  });
  const [rejectionReason, setRejectionReason] = useState("");

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('influencers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type assertion for the data
      const typedData = (data || []).map(item => ({
        ...item,
        social_links: (item.social_links || {}) as Influencer['social_links'],
        contact_info: (item.contact_info || {}) as Influencer['contact_info'],
        expertise: (item.expertise || []) as string[],
        approval_status: (item.approval_status || 'pending') as Influencer['approval_status'],
      })) as Influencer[];
      
      setInfluencers(typedData);
    } catch (error) {
      console.error('Error fetching influencers:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في جلب بيانات المؤثرين" : "Failed to fetch influencers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfluencers();
  }, []);

  const handleApprove = async (influencer: Influencer) => {
    setUpdating(influencer.id);
    try {
      const { error } = await supabase
        .from('influencers')
        .update({
          approval_status: 'approved',
          approved_at: new Date().toISOString(),
          rejection_reason: null,
        })
        .eq('id', influencer.id);

      if (error) throw error;

      setInfluencers(prev => prev.map(inf => 
        inf.id === influencer.id 
          ? { ...inf, approval_status: 'approved' as const, rejection_reason: null }
          : inf
      ));

      toast({
        title: isRTL ? "تمت الموافقة" : "Approved",
        description: isRTL 
          ? `تمت الموافقة على ${influencer.name} كمؤثر`
          : `${influencer.name} has been approved as an influencer`,
      });
    } catch (error) {
      console.error('Error approving influencer:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في الموافقة على المؤثر" : "Failed to approve influencer",
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.influencer) return;
    
    setUpdating(rejectDialog.influencer.id);
    try {
      const { error } = await supabase
        .from('influencers')
        .update({
          approval_status: 'rejected',
          rejection_reason: rejectionReason || null,
        })
        .eq('id', rejectDialog.influencer.id);

      if (error) throw error;

      setInfluencers(prev => prev.map(inf => 
        inf.id === rejectDialog.influencer!.id 
          ? { ...inf, approval_status: 'rejected' as const, rejection_reason: rejectionReason }
          : inf
      ));

      toast({
        title: isRTL ? "تم الرفض" : "Rejected",
        description: isRTL 
          ? `تم رفض ${rejectDialog.influencer.name}`
          : `${rejectDialog.influencer.name} has been rejected`,
      });

      setRejectDialog({ open: false, influencer: null });
      setRejectionReason("");
    } catch (error) {
      console.error('Error rejecting influencer:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في رفض المؤثر" : "Failed to reject influencer",
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  const filteredInfluencers = influencers.filter(inf => {
    const matchesSearch = inf.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         inf.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || inf.approval_status === activeTab;
    return matchesSearch && matchesTab;
  });

  const counts = {
    pending: influencers.filter(inf => inf.approval_status === 'pending').length,
    approved: influencers.filter(inf => inf.approval_status === 'approved').length,
    rejected: influencers.filter(inf => inf.approval_status === 'rejected').length,
    all: influencers.length,
  };

  const InfluencerCard = ({ influencer }: { influencer: Influencer }) => (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={influencer.image || undefined} alt={influencer.name} />
            <AvatarFallback>{influencer.name.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          
          <div className="flex-1 space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg">{influencer.name}</h3>
                <p className="text-sm text-muted-foreground capitalize">{influencer.category}</p>
              </div>
              <Badge variant={
                influencer.approval_status === 'approved' ? 'default' :
                influencer.approval_status === 'pending' ? 'secondary' : 'destructive'
              }>
                {influencer.approval_status === 'approved' 
                  ? (isRTL ? "موافق عليه" : "Approved")
                  : influencer.approval_status === 'pending'
                  ? (isRTL ? "قيد المراجعة" : "Pending")
                  : (isRTL ? "مرفوض" : "Rejected")}
              </Badge>
            </div>

            {influencer.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">{influencer.description}</p>
            )}

            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{influencer.followers}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span>{influencer.price.toLocaleString()} SAR</span>
              </div>
            </div>

            <div className="flex gap-2">
              {influencer.social_links?.instagram && (
                <a href={influencer.social_links.instagram} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Instagram className="h-4 w-4 text-pink-500" />
                  </Button>
                </a>
              )}
              {influencer.social_links?.youtube && (
                <a href={influencer.social_links.youtube} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Youtube className="h-4 w-4 text-red-500" />
                  </Button>
                </a>
              )}
              {influencer.social_links?.tiktok && (
                <a href={influencer.social_links.tiktok} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <FaTiktok className="h-4 w-4" />
                  </Button>
                </a>
              )}
            </div>

            {influencer.expertise && influencer.expertise.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {influencer.expertise.slice(0, 3).map((exp, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {exp.replace(/_/g, ' ')}
                  </Badge>
                ))}
                {influencer.expertise.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{influencer.expertise.length - 3}
                  </Badge>
                )}
              </div>
            )}

            {influencer.contact_info?.email && (
              <p className="text-xs text-muted-foreground">
                {isRTL ? "البريد:" : "Email:"} {influencer.contact_info.email}
              </p>
            )}

            {influencer.rejection_reason && influencer.approval_status === 'rejected' && (
              <p className="text-xs text-destructive">
                {isRTL ? "سبب الرفض:" : "Reason:"} {influencer.rejection_reason}
              </p>
            )}

            {influencer.approval_status === 'pending' && (
              <div className="flex gap-2 pt-2">
                <Button 
                  size="sm" 
                  onClick={() => handleApprove(influencer)}
                  disabled={updating === influencer.id}
                >
                  {updating === influencer.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {isRTL ? "موافقة" : "Approve"}
                    </>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => setRejectDialog({ open: true, influencer })}
                  disabled={updating === influencer.id}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  {isRTL ? "رفض" : "Reject"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={isRTL ? "البحث عن مؤثر..." : "Search influencers..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={fetchInfluencers}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {isRTL ? "تحديث" : "Refresh"}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending" className="relative">
            <Clock className="h-4 w-4 mr-1" />
            {isRTL ? "قيد المراجعة" : "Pending"}
            {counts.pending > 0 && (
              <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {counts.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="h-4 w-4 mr-1" />
            {isRTL ? "موافق عليهم" : "Approved"} ({counts.approved})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <XCircle className="h-4 w-4 mr-1" />
            {isRTL ? "مرفوضين" : "Rejected"} ({counts.rejected})
          </TabsTrigger>
          <TabsTrigger value="all">
            {isRTL ? "الكل" : "All"} ({counts.all})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredInfluencers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {isRTL ? "لا يوجد مؤثرين في هذه الفئة" : "No influencers in this category"}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredInfluencers.map(influencer => (
                <InfluencerCard key={influencer.id} influencer={influencer} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={rejectDialog.open} onOpenChange={(open) => {
        setRejectDialog({ open, influencer: open ? rejectDialog.influencer : null });
        if (!open) setRejectionReason("");
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isRTL ? "رفض المؤثر" : "Reject Influencer"}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? `هل أنت متأكد من رفض ${rejectDialog.influencer?.name}؟`
                : `Are you sure you want to reject ${rejectDialog.influencer?.name}?`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {isRTL ? "سبب الرفض (اختياري)" : "Rejection Reason (optional)"}
            </label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={isRTL ? "اكتب سبب الرفض..." : "Enter rejection reason..."}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, influencer: null })}>
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={updating !== null}>
              {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRTL ? "تأكيد الرفض" : "Confirm Rejection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
